import type { Control, FieldValues, Path } from "react-hook-form";
import { useController, useWatch } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { numberFieldProps } from "@/lib/number-field";
import { DatePicker } from "@/components/ui/date-picker";

export type DiscountType = "None" | "Percentage" | "Fixed";

export const DISCOUNT_TYPES: { value: DiscountType; label: string }[] = [
  { value: "None", label: "No discount" },
  { value: "Percentage", label: "Percentage" },
  { value: "Fixed", label: "Fixed amount" },
];

export const discountTypeSchema = z.enum(["None", "Percentage", "Fixed"]);

/** Tolerates NaN (an emptied input) so `discountValueIssue`'s message shows instead of zod's raw type error. */
export const discountValueSchema = z.union([z.number(), z.nan()]).nullable();

/** Mirrors the backend guard: rejects a pasted or malformed figure before it reaches the apportionment. */
const MAX_DISCOUNT_VALUE = 1_000_000_000_000;

/** The value input only appears once a type is chosen: the backend rejects a value sent with type None. */
export function DiscountFields<T extends FieldValues>({
  control,
  typeName,
  valueName,
  currency,
  label = "Discount",
  compact = false,
}: {
  control: Control<T>;
  typeName: Path<T>;
  valueName: Path<T>;
  currency: string;
  label?: string;
  compact?: boolean;
}) {
  const type = useWatch({ control, name: typeName }) as DiscountType | null | undefined;
  // Registered unconditionally so switching to "No discount" can clear the hidden value; the server 422s a
  // value sent with type None.
  const { field: valueField } = useController({ control, name: valueName });
  const showValue = type === "Percentage" || type === "Fixed";
  const labelClass = compact ? "text-xs" : undefined;

  return (
    <div className="grid grid-cols-2 gap-3">
      <FormField
        control={control}
        name={typeName}
        render={({ field }) => (
          <FormItem className="min-w-0">
            <FormLabel className={labelClass}>{label}</FormLabel>
            <Select
              onValueChange={(val) => {
                const next = val as DiscountType;
                field.onChange(next);
                if (next === "None") valueField.onChange(null);
              }}
              value={(field.value as string) ?? "None"}
            >
              <FormControl>
                <SelectTrigger className="w-full min-w-0">
                  <SelectValue placeholder="No discount" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {DISCOUNT_TYPES.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {showValue && (
        <FormField
          control={control}
          name={valueName}
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel className={labelClass}>
                {type === "Percentage" ? "Percent off" : `Amount off (${currency || "?"})`}
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  max={type === "Percentage" ? "100" : undefined}
                  {...field}
                  {...numberFieldProps(field)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}

/** The discount as entered ("10%", "GBP 50"), for a schedule template that has no resolved amount yet. */
export function formatDiscount(
  type: string | null | undefined,
  value: number | string | null | undefined,
  currency: string,
): string {
  if (!type || type === "None" || value == null) return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return type === "Percentage" ? `${numeric}%` : formatCurrency(numeric, currency);
}

/**
 * Mirrors the backend's `InvoiceTotalsCalculator.ValidateShape`. Whether a discount exceeds its base needs
 * resolved prices, so that lives in `computeTotals`.
 */
export function discountValueIssue(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
): string | null {
  // No issue for a leftover value under None: it would be unclearable (the input is hidden), and
  // `discountPayload` sends null regardless.
  if (!type || type === "None") return null;
  if (value == null || Number.isNaN(value)) return "Enter a discount amount";
  if (!Number.isFinite(value)) return "Enter a valid discount amount";
  if (value < 0) return "Discount cannot be negative";
  if (value > MAX_DISCOUNT_VALUE) return "Discount amount is too large";
  if (type === "Percentage" && value > 100) return "Percentage cannot exceed 100";
  // The server stores money and percentages to 2dp and rejects anything finer rather than rounding
  // it, so the record always equals what was entered.
  if (!hasAtMostTwoDecimals(value)) return "Use at most 2 decimal places";
  return null;
}

function hasAtMostTwoDecimals(value: number): boolean {
  const scaled = value * 100;
  // Tolerance rather than `% 1 === 0`: 10.005 * 100 is 1000.4999999999999 in binary, and an exact
  // 2dp figure like 12.34 can land a hair off its integer too.
  return Math.abs(scaled - Math.round(scaled)) < 1e-9;
}

/** What the API accepts: `null` for the value whenever there is no discount. */
export function discountPayload(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
): { discountType: DiscountType; discountValue: number | null } {
  const resolved = type ?? "None";
  return {
    discountType: resolved,
    discountValue: resolved === "None" ? null : (value ?? 0),
  };
}

// Live totals preview, mirroring the backend's InvoiceTotalsCalculator (the server stays the authority).
// Integer pence throughout so the apportionment adds up exactly.

export interface TotalsLineInput {
  quantity: number;
  unitPrice: number;
  discountType?: DiscountType | null;
  discountValue?: number | null;
  taxRatePercent: number;
}

export interface TotalsLineResult {
  subTotal: number;
  discountAmount: number;
  allocatedInvoiceDiscount: number;
  taxableAmount: number;
  taxAmount: number;
}

export interface Totals {
  lines: TotalsLineResult[];
  subTotal: number;
  lineDiscountTotal: number;
  discountAmount: number;
  discountTotal: number;
  taxAmount: number;
  totalAmount: number;
  /**
   * Discounts larger than their base. The server 422s rather than caps these, so the preview reports them
   * instead of clamping.
   */
  exceeds: {
    lines: boolean[];
    invoice: boolean;
  };
}

// Cash rounds half-away-from-zero (numeric(18,2)), tax half-to-even, as the server does; see eng-docs
// "AR - Invoice Discounts & Product Bulk Upload" §4.1. Integer numerators only: 1.15 * 0.5 * 100 floors to 57p, not 58p.

function divRoundAwayFromZero(numerator: number, denominator: number): number {
  const quotient = Math.floor(numerator / denominator);
  const remainder = numerator - quotient * denominator;
  return remainder * 2 >= denominator ? quotient + 1 : quotient;
}

function divRoundHalfEven(numerator: number, denominator: number): number {
  const quotient = Math.floor(numerator / denominator);
  const remainder = numerator - quotient * denominator;
  if (remainder * 2 > denominator) return quotient + 1;
  if (remainder * 2 < denominator) return quotient;
  return quotient % 2 === 0 ? quotient : quotient + 1;
}

/** `Math.round` is exact here; it is the later division that must stay in integer space. */
function scaled(value: number, places: number): number {
  return Math.round((Number.isFinite(value) ? value : 0) * 10 ** places);
}

// Quantity carries 4dp on the server and unit price 2dp, so the exact product is 6dp; dividing the
// scaled integers by 10^4 lands on pence.
function grossPence(quantity: number, unitPrice: number): number {
  return divRoundAwayFromZero(scaled(quantity, 4) * scaled(unitPrice, 2), 10_000);
}

function resolveDiscountPence(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
  basePence: number,
): number {
  if (!type || type === "None" || value == null || !Number.isFinite(value) || value <= 0) return 0;
  // Percentages are capped at 2dp by discountValueIssue, so scaling by 100 is lossless.
  if (type === "Percentage") return divRoundAwayFromZero(basePence * scaled(value, 2), 10_000);
  return scaled(value, 2);
}

export function computeTotals(
  lines: TotalsLineInput[],
  invoiceDiscountType?: DiscountType | null,
  invoiceDiscountValue?: number | null,
): Totals {
  const resolved = lines.map((line) => {
    const gross = grossPence(line.quantity || 0, line.unitPrice || 0);
    // NOT clamped: the server 422s Invoice.DiscountExceedsAmount, so the overage goes through `exceeds`.
    const discount = resolveDiscountPence(line.discountType, line.discountValue, gross);
    return {
      gross,
      discount,
      exceeds: discount > gross,
      net: gross - discount,
      taxRatePercent: line.taxRatePercent || 0,
      allocation: 0,
      fraction: 0,
    };
  });

  const subTotal = resolved.reduce((sum, line) => sum + line.gross, 0);
  const lineDiscountTotal = resolved.reduce((sum, line) => sum + line.discount, 0);
  const discountBase = subTotal - lineDiscountTotal;

  // Equal to the base is legal and yields a zero-total invoice; only strictly greater is rejected.
  const invoiceDiscount = resolveDiscountPence(
    invoiceDiscountType,
    invoiceDiscountValue,
    discountBase,
  );
  const invoiceDiscountExceeds = invoiceDiscount > discountBase;

  // Largest remainder: floor each share, then give the leftover pence to the biggest fractions, so
  // the per-line shares always add back to the invoice discount exactly.
  if (invoiceDiscount > 0 && discountBase > 0) {
    let allocated = 0;
    for (const line of resolved) {
      const exact = (invoiceDiscount * line.net) / discountBase;
      line.allocation = Math.floor(exact);
      line.fraction = exact - line.allocation;
      allocated += line.allocation;
    }
    const byRemainder = [...resolved].sort((a, b) => b.fraction - a.fraction);
    for (let i = 0; i < invoiceDiscount - allocated; i++) {
      const line = byRemainder[i];
      if (line) line.allocation += 1;
    }
  }

  const results: TotalsLineResult[] = [];
  let taxTotal = 0;
  let taxableTotal = 0;

  for (const line of resolved) {
    const taxable = line.net - line.allocation;
    const tax = divRoundHalfEven(taxable * scaled(line.taxRatePercent, 4), 1_000_000);
    taxableTotal += taxable;
    taxTotal += tax;
    results.push({
      subTotal: line.gross / 100,
      discountAmount: line.discount / 100,
      allocatedInvoiceDiscount: line.allocation / 100,
      taxableAmount: taxable / 100,
      taxAmount: tax / 100,
    });
  }

  return {
    lines: results,
    subTotal: subTotal / 100,
    lineDiscountTotal: lineDiscountTotal / 100,
    discountAmount: invoiceDiscount / 100,
    discountTotal: (lineDiscountTotal + invoiceDiscount) / 100,
    taxAmount: taxTotal / 100,
    totalAmount: (taxableTotal + taxTotal) / 100,
    exceeds: {
      lines: resolved.map((line) => line.exceeds),
      invoice: invoiceDiscountExceeds,
    },
  };
}

/** The server's over-discount messages, so callers can block a submit that would 422. */
export function discountExceedsIssues(totals: Totals): {
  lines: (string | null)[];
  invoice: string | null;
} {
  return {
    lines: totals.exceeds.lines.map((over) =>
      over ? "Discount is more than this line's total" : null,
    ),
    invoice: totals.exceeds.invoice
      ? "Discount is more than the invoice total after line discounts"
      : null,
  };
}

// Early payment discount: not apportioned before tax. Issued at full price, it comes off the gross total if paid
// in time (a credit note corrects the VAT). See eng-docs "AR - Early Payment Discounts".

/** Today plus `days`, as the yyyy-MM-dd the API and the date picker use. */
export function isoDateFromToday(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The discount in currency units against the gross total, rounded as the server rounds it (half away from zero). */
export function earlyPaymentDiscountAmount(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
  totalAmount: number,
): number {
  return resolveDiscountPence(type, value, scaled(totalAmount, 2)) / 100;
}

/** Mirrors the server's checks: shape, amount against the total, and the window (today to the due date). */
export function earlyPaymentDiscountIssues(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
  expiresOn: string | null | undefined,
  totalAmount: number,
  paymentTermsDays: number | null | undefined,
): { value: string | null; expiresOn: string | null } {
  if (!type || type === "None") return { value: null, expiresOn: null };
  const valueIssue =
    discountValueIssue(type, value) ??
    (type === "Fixed" && value != null && value >= totalAmount && totalAmount > 0
      ? "Must be less than the invoice total"
      : null);
  let expiryIssue: string | null = null;
  if (!expiresOn) expiryIssue = "Pick the last day to pay at the discount";
  else if (expiresOn < isoDateFromToday(0)) expiryIssue = "Cannot be in the past";
  else if (paymentTermsDays != null && expiresOn > isoDateFromToday(paymentTermsDays))
    expiryIssue = "Must be on or before the due date";
  return { value: valueIssue, expiresOn: expiryIssue };
}

/** The request shape, or null when there is no discount. */
export function earlyPaymentDiscountPayload(
  type: DiscountType | null | undefined,
  value: number | null | undefined,
  expiresOn: string | null | undefined,
): { type: DiscountType; value: number | null; expiresOn: string | null } | null {
  if (!type || type === "None") return null;
  return { type, value: value ?? 0, expiresOn: expiresOn || null };
}

/** Choosing a type fills the date with today + 10 days, capped at the due date, which is what most sellers offer. */
export function EarlyPaymentDiscountFields<T extends FieldValues>({
  control,
  typeName,
  valueName,
  expiresOnName,
  currency,
  paymentTermsDays,
}: {
  control: Control<T>;
  typeName: Path<T>;
  valueName: Path<T>;
  expiresOnName: Path<T>;
  currency: string;
  paymentTermsDays: number | null | undefined;
}) {
  const type = useWatch({ control, name: typeName }) as DiscountType | null | undefined;
  const { field: expiresOnField } = useController({ control, name: expiresOnName });
  const { field: valueField } = useController({ control, name: valueName });
  const showDetail = type === "Percentage" || type === "Fixed";

  return (
    <div className="space-y-3">
      {/* One column: it sits in the editor's narrow summary card, where two would clip "Fixed amount". */}
      <div className="grid grid-cols-1 gap-3">
        <FormField
          control={control}
          name={typeName}
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel>Early payment discount</FormLabel>
              <Select
                onValueChange={(val) => {
                  const next = val as DiscountType;
                  field.onChange(next);
                  if (next === "None") {
                    valueField.onChange(null);
                    expiresOnField.onChange(null);
                  } else if (!expiresOnField.value) {
                    expiresOnField.onChange(isoDateFromToday(Math.min(10, paymentTermsDays ?? 10)));
                  }
                }}
                value={(field.value as string) ?? "None"}
              >
                <FormControl>
                  <SelectTrigger className="w-full min-w-0">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="Percentage">Percentage</SelectItem>
                  <SelectItem value="Fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {showDetail && (
          <FormField
            control={control}
            name={valueName}
            render={({ field }) => (
              <FormItem className="min-w-0">
                <FormLabel>{type === "Percentage" ? "Percent off" : `Amount off (${currency || "?"})`}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="any"
                    min="0"
                    max={type === "Percentage" ? "100" : undefined}
                    {...field}
                    {...numberFieldProps(field)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
      {showDetail && (
        <FormField
          control={control}
          name={expiresOnName}
          render={({ field }) => (
            <FormItem>
              <FormLabel>If paid by</FormLabel>
              <FormControl>
                <DatePicker value={field.value as string | null} onChange={field.onChange} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
